import { useEffect, useMemo, useState, useCallback } from 'react';
import throttle from 'lodash.throttle';
import debounce from 'lodash.debounce';
import { readCSSVar } from '../../utils/cssVarManipulation';
import type { Nullable } from '../../types';
import type { ListConfig, NavigationButton } from './types';

const TIMEOUT: number = 300;

const DEFAULT_LIST_CONFIG = {
  columnWidth: 0,
  columnCount: 0,
  columnSpacing: 0,
};

const getListConfig = (component: HTMLElement): Nullable<ListConfig> => {
  if (!component) return DEFAULT_LIST_CONFIG;

  const columnWidth = readCSSVar(component, '--column-width');
  const columnCount = readCSSVar(component, '--column-count');
  const columnSpacing = readCSSVar(component, '--item-spacing');

  return {
    columnWidth: parseInt(columnWidth),
    columnCount: parseInt(columnCount),
    columnSpacing: parseInt(columnSpacing),
  };
};

export const useColumnCount = (
  component: HTMLElement,
  scrollable: boolean = false
): number => {
  const [count, setCount] = useState<number>(Infinity);
  const { columnCount } = useMemo<ListConfig>(
    () => getListConfig(component),
    [component]
  );

  useEffect(() => {
    if (scrollable) return;
    if (columnCount) setCount(columnCount);
  }, [scrollable, columnCount]);

  return count;
};

export const useNavigationButton = (
  component: HTMLElement,
  listElem: HTMLElement,
  itemCount: number
): NavigationButton => {
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [listConfig, setListConfig] = useState<ListConfig>(DEFAULT_LIST_CONFIG);

  const { columnWidth, columnSpacing, columnCount } = listConfig;
  const columnSize = columnWidth + columnSpacing;

  useEffect(() => {
    if (listElem) {
      const scrollHandler = debounce(
        () => setScrollPosition(listElem.scrollLeft),
        TIMEOUT
      );

      listElem.addEventListener('scroll', scrollHandler);
      return () => listElem.removeEventListener('scroll', scrollHandler);
    }
  }, [listElem]);

  useEffect(() => {
    if (component) {
      setListConfig(getListConfig(component));

      const resizeHandler = debounce(
        () => setListConfig(getListConfig(component)),
        TIMEOUT
      );

      window.addEventListener('resize', resizeHandler);
      return () => window.removeEventListener('resize', resizeHandler);
    }
  }, [component]);

  const isPrevEnabled = scrollPosition > 0;
  const isNextEnabled =
    itemCount > columnCount &&
    scrollPosition < columnSize * itemCount - columnSize * columnCount;

  const next = useCallback(
    () => (listElem.scrollLeft += columnSize),
    [columnSize, listElem]
  );
  const prev = useCallback(
    () => (listElem.scrollLeft -= columnSize),
    [columnSize, listElem]
  );
  const nextSlide = throttle(next, TIMEOUT);
  const prevSlide = throttle(prev, TIMEOUT);

  return {
    prevSlide,
    nextSlide,
    isPrevEnabled,
    isNextEnabled,
  };
};
